import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import ExportModal from "@/components/ExportModal";
import Dispatch from "@/pages/Dispatch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Search, Plus, Users, Download, Radio, Shield, FileText, Power, Edit,
  RotateCcw, Save, X, Car, Clock, MapPin, Activity, Filter,
  ChevronDown, ChevronRight, ExternalLink, Navigation, ArrowLeft, Phone, Calendar, Copy,
  CreditCard, Award, Check, Trash2, ArrowUpDown
} from "lucide-react";
import { format } from "date-fns";

const hasActivePlaque = (unit) => {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  return (unit?.plaques || []).some((p) => !p.returned_at && (!p.expires_at || p.expires_at >= today));
};

export default function Units() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [deactivatedUnits, setDeactivatedUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitIncidents, setUnitIncidents] = useState({ responded: [], investigating: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportData, setExportData] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [exportDownloadUrl, setExportDownloadUrl] = useState("");
  const [showMasterExport, setShowMasterExport] = useState(false);
  const [masterExportStartDate, setMasterExportStartDate] = useState("");
  const [masterExportEndDate, setMasterExportEndDate] = useState("");
  const [unitPatrolStats, setUnitPatrolStats] = useState(null);
  const [unitPatrolLogs, setUnitPatrolLogs] = useState([]);
  // Patrol tab sub-view: "logs" = the original per-event list, "sessions" =
  // each login/logout paired into one session row.
  const [patrolView, setPatrolView] = useState("logs");
  const [detailTab, setDetailTab] = useState("incidents");
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [locationHistory, setLocationHistory] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);

  // Filter states
  const [incidentStartDate, setIncidentStartDate] = useState("");
  const [incidentEndDate, setIncidentEndDate] = useState("");
  const [incidentTypeFilter, setIncidentTypeFilter] = useState("all");
  const [patrolStartDate, setPatrolStartDate] = useState("");
  const [patrolEndDate, setPatrolEndDate] = useState("");
  const [locationStartDate, setLocationStartDate] = useState("");
  const [locationEndDate, setLocationEndDate] = useState("");

  // Audit tab state
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditStartDate, setAuditStartDate] = useState("");
  const [auditEndDate, setAuditEndDate] = useState("");
  const [auditIncidentSearch, setAuditIncidentSearch] = useState("");
  const [auditAddressSearch, setAuditAddressSearch] = useState("");
  const [auditTypeFilter, setAuditTypeFilter] = useState("all");
  const [auditUnitFilter, setAuditUnitFilter] = useState("all");
  const [auditActivityFilter, setAuditActivityFilter] = useState("both"); // "incidents", "live_calls", "patrol", "both"
  // Audit incident-level filters
  const [auditDispatcherFilter, setAuditDispatcherFilter] = useState("");
  const [auditHasAnyProfile, setAuditHasAnyProfile] = useState(false);
  const [auditSpecificProfile, setAuditSpecificProfile] = useState("");
  const [auditReadyForArrest, setAuditReadyForArrest] = useState(false);
  const [auditProfileArrested, setAuditProfileArrested] = useState(false);
  const [auditArrests, setAuditArrests] = useState(false);
  const [auditHateCrime, setAuditHateCrime] = useState(false);
  const [auditWeapon, setAuditWeapon] = useState(false);
  const [auditInjuries, setAuditInjuries] = useState(false);
  const [auditMinorElderly, setAuditMinorElderly] = useState(false);
  const [auditPdActivated, setAuditPdActivated] = useState(false);
  const [auditHotlineCall, setAuditHotlineCall] = useState(false);
  const [auditReportFiled, setAuditReportFiled] = useState(false);
  const [auditFootageRequested, setAuditFootageRequested] = useState(false);
  const [auditFootageObtained, setAuditFootageObtained] = useState(false);
  const [showAuditIncidentFilters, setShowAuditIncidentFilters] = useState(false);
  // Reference data for audit filters
  const [dispatchers, setDispatchers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [expandedUnits, setExpandedUnits] = useState({});
  const [mainTab, setMainTab] = useState("units");
  const [showAuditExportDialog, setShowAuditExportDialog] = useState(false);
  const [patrolExportStartDate, setPatrolExportStartDate] = useState("");
  const [patrolExportEndDate, setPatrolExportEndDate] = useState("");
  const [showPatrolExportDialog, setShowPatrolExportDialog] = useState(false);

  // Year-average comparison state (Incidents tab)
  const [yearStatsYear, setYearStatsYear] = useState(new Date().getFullYear());
  const [yearStats, setYearStats] = useState(null); // { year, units: [{unit, incidents}], available_years }
  const [yearStatsLoading, setYearStatsLoading] = useState(false);

  // ID/Plaque records state
  const emptyCreditCardForm = { card_number: "", issued_at: "", expires_at: "", returned_at: "", old_card_returned: false, notes: "" };
  const emptyPlaqueForm = { plaque_number: "", issued_at: "", expires_at: "", returned_at: "", notes: "" };
  const [idCardForm, setCreditCardForm] = useState(emptyCreditCardForm);
  const [plaqueForm, setPlaqueForm] = useState(emptyPlaqueForm);
  const [editingCreditCard, setEditingCreditCard] = useState(null);
  const [editingPlaque, setEditingPlaque] = useState(null);
  const [showCreditCardDialog, setShowCreditCardDialog] = useState(false);
  const [showPlaqueDialog, setShowPlaqueDialog] = useState(false);

  // Quick week export state
  const [showQuickExportDialog, setShowQuickExportDialog] = useState(false);
  const [quickExportWeek, setQuickExportWeek] = useState("");
  const [quickExportLoading, setQuickExportLoading] = useState(false);

  const [newUnit, setNewUnit] = useState({ name: "", is_dispatcher: false, is_investigator: false, is_patrol: false });
  const [editUnit, setEditUnit] = useState({});

  useEffect(() => {
    fetchUnits();
    fetchIncidentTypes();
    axios.get(`${API}/units/dispatchers`, { withCredentials: true })
      .then(res => setDispatchers(res.data || []))
      .catch(() => {});
    axios.get(`${API}/perpetrator-profiles`, { withCredentials: true })
      .then(res => setProfiles(res.data || []))
      .catch(() => {});
  }, []);

  const fetchUnits = async () => {
    try {
      const [activeRes, deactivatedRes] = await Promise.all([
        axios.get(`${API}/units`, { withCredentials: true }),
        user?.role === "admin" 
          ? axios.get(`${API}/units/deactivated`, { withCredentials: true }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] })
      ]);
      setUnits(activeRes.data);
      setDeactivatedUnits(deactivatedRes.data);
    } catch (error) {
      toast.error("Failed to load units");
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidentTypes = async () => {
    try {
      const response = await axios.get(`${API}/incident-types`, { withCredentials: true });
      setIncidentTypes(response.data || []);
    } catch (error) {
      console.error("Failed to fetch incident types:", error);
    }
  };

  const fetchUnitIncidents = async (unitName) => {
    try {
      const response = await axios.get(`${API}/units/${unitName}/incidents`, { withCredentials: true });
      setUnitIncidents(response.data);
    } catch (error) {
      console.error("Failed to fetch unit incidents:", error);
    }
  };

  const handleSelectUnit = (unit) => {
    setSelectedUnit(unit);
    setDetailTab("incidents");
    setIncidentStartDate(""); setIncidentEndDate(""); setIncidentTypeFilter("all");
    setPatrolStartDate(""); setPatrolEndDate("");
    setLocationStartDate(""); setLocationEndDate("");
    fetchUnitIncidents(unit.name);
    fetchUnitPatrolData(unit.name);
    fetchLocationHistory(unit.name);
  };

  const fetchUnitPatrolData = async (unitName) => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/patrol/stats/unit/${unitName}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/patrol/logs?unit_name=${unitName}`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setUnitPatrolStats(statsRes.data);
      setUnitPatrolLogs(logsRes.data);
    } catch (error) {
      setUnitPatrolStats(null);
      setUnitPatrolLogs([]);
    }
  };

  // Year-average fetch — runs whenever the chosen year changes (unit can
  // come and go without refetching since the dataset is keyed by unit).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setYearStatsLoading(true);
      try {
        const res = await axios.get(`${API}/units/year-stats?year=${yearStatsYear}`, { withCredentials: true });
        if (!cancelled) setYearStats(res.data);
      } catch {
        if (!cancelled) setYearStats(null);
      } finally {
        if (!cancelled) setYearStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [yearStatsYear]);

  // ID Cards / Plaques — open add dialog with empty form
  const openNewCreditCard = () => {
    setEditingCreditCard(null);
    setCreditCardForm(emptyCreditCardForm);
    setShowCreditCardDialog(true);
  };
  const openEditCreditCard = (rec) => {
    setEditingCreditCard(rec);
    setCreditCardForm({
      card_number: rec.card_number || "",
      issued_at: rec.issued_at || "",
      expires_at: rec.expires_at || "",
      returned_at: rec.returned_at || "",
      old_card_returned: !!rec.old_card_returned,
      notes: rec.notes || "",
    });
    setShowCreditCardDialog(true);
  };
  const saveCreditCard = async () => {
    if (!selectedUnit) return;
    try {
      const payload = { ...idCardForm };
      if (editingCreditCard) {
        await axios.put(`${API}/units/${selectedUnit.unit_id}/id-cards/${editingCreditCard.record_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/units/${selectedUnit.unit_id}/id-cards`, payload, { withCredentials: true });
      }
      setShowCreditCardDialog(false);
      const refreshed = await axios.get(`${API}/units`, { withCredentials: true });
      setUnits(refreshed.data);
      const updated = refreshed.data.find(u => u.unit_id === selectedUnit.unit_id);
      if (updated) setSelectedUnit(updated);
      toast.success(editingCreditCard ? "ID card updated" : "ID card added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ID card");
    }
  };
  const markCreditCardReturned = async (rec) => {
    if (!selectedUnit) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        card_number: rec.card_number || null,
        issued_at: rec.issued_at || null,
        expires_at: rec.expires_at || null,
        returned_at: today,
        old_card_returned: !!rec.old_card_returned,
        notes: rec.notes || null,
      };
      await axios.put(`${API}/units/${selectedUnit.unit_id}/id-cards/${rec.record_id}`, payload, { withCredentials: true });
      const refreshed = await axios.get(`${API}/units`, { withCredentials: true });
      setUnits(refreshed.data);
      const updated = refreshed.data.find(u => u.unit_id === selectedUnit.unit_id);
      if (updated) setSelectedUnit(updated);
      toast.success("Marked as returned");
    } catch (e) {
      toast.error("Failed to mark returned");
    }
  };

  const openNewPlaque = () => {
    setEditingPlaque(null);
    setPlaqueForm(emptyPlaqueForm);
    setShowPlaqueDialog(true);
  };
  const openEditPlaque = (rec) => {
    setEditingPlaque(rec);
    setPlaqueForm({
      plaque_number: rec.plaque_number || "",
      issued_at: rec.issued_at || "",
      expires_at: rec.expires_at || "",
      returned_at: rec.returned_at || "",
      notes: rec.notes || "",
    });
    setShowPlaqueDialog(true);
  };
  const savePlaque = async () => {
    if (!selectedUnit) return;
    try {
      const payload = { ...plaqueForm };
      if (editingPlaque) {
        await axios.put(`${API}/units/${selectedUnit.unit_id}/plaques/${editingPlaque.record_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/units/${selectedUnit.unit_id}/plaques`, payload, { withCredentials: true });
      }
      setShowPlaqueDialog(false);
      const refreshed = await axios.get(`${API}/units`, { withCredentials: true });
      setUnits(refreshed.data);
      const updated = refreshed.data.find(u => u.unit_id === selectedUnit.unit_id);
      if (updated) setSelectedUnit(updated);
      toast.success(editingPlaque ? "Plaque updated" : "Plaque added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save plaque");
    }
  };
  const markPlaqueReturned = async (rec) => {
    if (!selectedUnit) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        plaque_number: rec.plaque_number || null,
        issued_at: rec.issued_at || null,
        expires_at: rec.expires_at || null,
        returned_at: today,
        notes: rec.notes || null,
      };
      await axios.put(`${API}/units/${selectedUnit.unit_id}/plaques/${rec.record_id}`, payload, { withCredentials: true });
      const refreshed = await axios.get(`${API}/units`, { withCredentials: true });
      setUnits(refreshed.data);
      const updated = refreshed.data.find(u => u.unit_id === selectedUnit.unit_id);
      if (updated) setSelectedUnit(updated);
      toast.success("Marked as returned");
    } catch (e) {
      toast.error("Failed to mark returned");
    }
  };

  const fetchLocationHistory = async (unitName, startDate = "", endDate = "") => {
    try {
      setLocationLoading(true);
      let url = `${API}/location/history/unit/${unitName}`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += `?${params.join('&')}`;
      const response = await axios.get(url, { withCredentials: true });
      setLocationHistory(response.data.history || []);
    } catch (error) {
      setLocationHistory([]);
    } finally {
      setLocationLoading(false);
    }
  };

  const fetchAuditData = async () => {
    try {
      setAuditLoading(true);
      const params = new URLSearchParams();
      if (auditStartDate) params.append("start_date", auditStartDate);
      if (auditEndDate) params.append("end_date", auditEndDate);
      if (auditIncidentSearch) params.append("incident_search", auditIncidentSearch);
      if (auditAddressSearch) params.append("address_search", auditAddressSearch);
      if (auditTypeFilter && auditTypeFilter !== "all") params.append("incident_type", auditTypeFilter);
      if (auditUnitFilter && auditUnitFilter !== "all") params.append("unit_name", auditUnitFilter);
      const response = await axios.get(`${API}/units/audit/all?${params}`, { withCredentials: true });
      setAuditData(response.data || []);
    } catch (error) {
      toast.error("Failed to load audit data");
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleUnitExpansion = (unitName) => {
    setExpandedUnits(prev => ({ ...prev, [unitName]: !prev[unitName] }));
  };

  // Filtered data
  const filteredRespondedIncidents = useMemo(() => {
    return unitIncidents.responded.filter(inc => {
      if (incidentTypeFilter !== "all" && inc.incident_type !== incidentTypeFilter) return false;
      if (incidentStartDate && inc.date_time < incidentStartDate) return false;
      if (incidentEndDate && inc.date_time > incidentEndDate) return false;
      return true;
    });
  }, [unitIncidents.responded, incidentTypeFilter, incidentStartDate, incidentEndDate]);

  const filteredInvestigatingIncidents = useMemo(() => {
    return unitIncidents.investigating.filter(inc => {
      if (incidentTypeFilter !== "all" && inc.incident_type !== incidentTypeFilter) return false;
      if (incidentStartDate && inc.date_time < incidentStartDate) return false;
      if (incidentEndDate && inc.date_time > incidentEndDate) return false;
      return true;
    });
  }, [unitIncidents.investigating, incidentTypeFilter, incidentStartDate, incidentEndDate]);

  const filteredPatrolLogs = useMemo(() => {
    return unitPatrolLogs.filter(log => {
      if (patrolStartDate && log.timestamp < patrolStartDate) return false;
      if (patrolEndDate && log.timestamp > patrolEndDate) return false;
      return true;
    });
  }, [unitPatrolLogs, patrolStartDate, patrolEndDate]);

  // Pair the per-event logs into sessions. A logout/leave event already carries
  // session_start, the timestamp (= end) and the duration; we match it back to
  // its login/join event (same car, login time ≈ session_start) to surface who
  // logged in vs out. Logins with no matching end are shown as active sessions.
  const filteredPatrolSessions = useMemo(() => {
    const asc = [...filteredPatrolLogs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const logins = asc.filter(l => l.action === "login" || l.action === "join");
    const ends = asc.filter(l => l.action === "logout" || l.action === "leave");
    const usedLogin = new Set();
    const sessions = [];

    for (const end of ends) {
      let login = null;
      if (end.session_start) {
        login = logins.find(l =>
          l.car_id === end.car_id && !usedLogin.has(l.log_id) &&
          Math.abs(new Date(l.timestamp) - new Date(end.session_start)) < 60000
        );
      }
      if (!login) {
        // Fallback: the latest unused login on the same car at/before the end.
        const cands = logins.filter(l =>
          l.car_id === end.car_id && !usedLogin.has(l.log_id) &&
          new Date(l.timestamp) <= new Date(end.timestamp)
        );
        login = cands.length ? cands[cands.length - 1] : null;
      }
      if (login) usedLogin.add(login.log_id);
      sessions.push({
        key: end.log_id,
        car_name: end.car_name,
        start: end.session_start || login?.timestamp || null,
        end: end.timestamp,
        duration: end.session_duration_minutes || 0,
        action: end.action,
        parked_location: end.parked_location,
        logout_note: end.logout_note,
        fuel_level: end.fuel_level,
        login_by: login?.performed_by_name || null,
        logout_by: end.performed_by_name || null,
        ongoing: false,
      });
    }

    // Logins that never got a matching logout/leave → still active.
    for (const login of logins) {
      if (usedLogin.has(login.log_id)) continue;
      sessions.push({
        key: login.log_id,
        car_name: login.car_name,
        start: login.timestamp,
        end: null,
        duration: 0,
        action: login.action,
        login_by: login.performed_by_name || null,
        ongoing: true,
      });
    }

    sessions.sort((a, b) => new Date(b.end || b.start) - new Date(a.end || a.start));
    return sessions;
  }, [filteredPatrolLogs]);

  const filteredLocationHistory = useMemo(() => {
    return locationHistory.filter(loc => {
      if (locationStartDate && loc.timestamp < locationStartDate) return false;
      if (locationEndDate && loc.timestamp > locationEndDate) return false;
      return true;
    });
  }, [locationHistory, locationStartDate, locationEndDate]);

  const handleCreateUnit = async () => {
    try {
      const response = await axios.post(`${API}/units`, newUnit, { withCredentials: true });
      setUnits([...units, response.data].sort((a, b) => {
        const numA = parseInt(a.name.replace("CH", ""));
        const numB = parseInt(b.name.replace("CH", ""));
        return numA - numB;
      }));
      setShowNewDialog(false);
      setNewUnit({ name: "", is_dispatcher: false, is_investigator: false, is_patrol: false });
      toast.success("Unit created");
    } catch (error) {
      toast.error("Failed to create unit");
    }
  };

  const handleUpdateUnit = async () => {
    try {
      const response = await axios.put(`${API}/units/${editUnit.unit_id}`, {
        name: editUnit.name, is_dispatcher: editUnit.is_dispatcher,
        is_investigator: editUnit.is_investigator, is_patrol: editUnit.is_patrol
      }, { withCredentials: true });
      setUnits(units.map(u => u.unit_id === editUnit.unit_id ? response.data : u));
      if (selectedUnit?.unit_id === editUnit.unit_id) setSelectedUnit(response.data);
      setShowEditDialog(false);
      toast.success("Unit updated");
    } catch (error) {
      toast.error("Failed to update unit");
    }
  };

  const handleDeactivateUnit = async (unitId, unitName) => {
    try {
      await axios.delete(`${API}/units/${unitId}`, { withCredentials: true });
      const deactivatedUnit = units.find(u => u.unit_id === unitId);
      setUnits(units.filter(u => u.unit_id !== unitId));
      if (deactivatedUnit) setDeactivatedUnits([...deactivatedUnits, { ...deactivatedUnit, is_active: false }]);
      if (selectedUnit?.unit_id === unitId) {
        setSelectedUnit(null);
        setUnitIncidents({ responded: [], investigating: [] });
      }
      toast.success(`Unit ${unitName} deactivated`);
    } catch (error) {
      toast.error("Failed to deactivate unit");
    }
  };

  const handleReactivateUnit = async (unitId) => {
    try {
      const response = await axios.post(`${API}/units/${unitId}/reactivate`, {}, { withCredentials: true });
      setDeactivatedUnits(deactivatedUnits.filter(u => u.unit_id !== unitId));
      setUnits([...units, response.data].sort((a, b) => parseInt(a.name.replace("CH", "")) - parseInt(b.name.replace("CH", ""))));
      toast.success("Unit reactivated");
    } catch (error) {
      toast.error("Failed to reactivate unit");
    }
  };

  const handleExportFiltered = async (type) => {
    if (!selectedUnit) return;
    let dataToExport = [];
    let filename = "";
    if (type === "responded") {
      dataToExport = filteredRespondedIncidents;
      filename = `${selectedUnit.name}_responded_${format(new Date(), "yyyy-MM-dd")}.csv`;
    } else if (type === "investigating") {
      dataToExport = filteredInvestigatingIncidents;
      filename = `${selectedUnit.name}_investigating_${format(new Date(), "yyyy-MM-dd")}.csv`;
    } else if (type === "patrol") {
      dataToExport = filteredPatrolLogs;
      filename = `${selectedUnit.name}_patrol_${format(new Date(), "yyyy-MM-dd")}.csv`;
    } else if (type === "location") {
      dataToExport = filteredLocationHistory;
      filename = `${selectedUnit.name}_location_${format(new Date(), "yyyy-MM-dd")}.csv`;
    }
    if (dataToExport.length === 0) { toast.info("No data to export"); return; }
    const headers = Object.keys(dataToExport[0]);
    const csvRows = [headers.join(",")];
    dataToExport.forEach(row => {
      const values = headers.map(h => {
        let val = row[h];
        if (val === null || val === undefined) val = "";
        if (typeof val === "object") val = JSON.stringify(val);
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });
    setExportData(csvRows.join("\n"));
    setExportFilename(filename);
    setExportDownloadUrl("");
    setShowExportDialog(true);
  };

  const handleGlobalPatrolExport = async (exportType) => {
    try {
      toast.info("Preparing patrol export...");
      let url = exportType === "hours" ? `${API}/download/patrol-hours` : `${API}/download/patrol-logs`;
      const params = [];
      if (patrolExportStartDate) params.push(`start_date=${patrolExportStartDate}`);
      if (patrolExportEndDate) params.push(`end_date=${patrolExportEndDate}`);
      if (params.length) url += `?${params.join('&')}`;
      let filename = exportType === "hours" ? `patrol_hours_all_${format(new Date(), "yyyy-MM-dd")}.csv` : `patrol_logs_all_${format(new Date(), "yyyy-MM-dd")}.csv`;
      const response = await axios.get(url, { withCredentials: true, responseType: "text" });
      if (!response.data) { toast.info("No data to export"); return; }
      setExportData(response.data);
      setExportFilename(filename);
      setExportDownloadUrl(url);
      setShowExportDialog(true);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleMasterExport = async () => {
    try {
      toast.info("Preparing master export...");
      let url = `${API}/download/units-master`;
      const params = [];
      if (masterExportStartDate) params.push(`start_date=${masterExportStartDate}`);
      if (masterExportEndDate) params.push(`end_date=${masterExportEndDate}`);
      if (params.length) url += `?${params.join('&')}`;
      const response = await axios.get(url, { withCredentials: true, responseType: "text" });
      if (!response.data) { toast.info("No data to export"); return; }
      setExportData(response.data);
      setExportFilename(`units_master_report_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl(url);
      setShowExportDialog(true);
      setShowMasterExport(false);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleAuditExport = async () => {
    try {
      toast.info("Preparing audit export...");
      let url = `${API}/download/units-audit`;
      const params = [];
      if (auditStartDate) params.push(`start_date=${auditStartDate}`);
      if (auditEndDate) params.push(`end_date=${auditEndDate}`);
      if (auditIncidentSearch) params.push(`incident_search=${encodeURIComponent(auditIncidentSearch)}`);
      if (auditAddressSearch) params.push(`address_search=${encodeURIComponent(auditAddressSearch)}`);
      if (auditTypeFilter && auditTypeFilter !== "all") params.push(`incident_type=${encodeURIComponent(auditTypeFilter)}`);
      if (auditUnitFilter && auditUnitFilter !== "all") params.push(`unit_name=${encodeURIComponent(auditUnitFilter)}`);
      if (auditActivityFilter && auditActivityFilter !== "both") params.push(`activity_filter=${encodeURIComponent(auditActivityFilter)}`);
      if (params.length) url += `?${params.join('&')}`;
      const response = await axios.get(url, { withCredentials: true, responseType: "text" });
      if (!response.data) { toast.info("No data to export"); return; }
      setExportData(response.data);
      setExportFilename(`units_audit_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl(url);
      setShowExportDialog(true);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Generate Friday-to-Friday week options (last 12 weeks)
  const getWeekOptions = () => {
    const weeks = [];
    const today = new Date();
    // Find the most recent Saturday (or today if it's Saturday)
    const dayOfWeek = today.getDay();
    const daysToSaturday = dayOfWeek >= 6 ? dayOfWeek - 6 : dayOfWeek + 1;
    const lastSaturday = new Date(today);
    lastSaturday.setDate(today.getDate() - daysToSaturday);
    lastSaturday.setHours(0, 0, 0, 0);

    // Generate last 12 weeks (Saturday to Saturday)
    for (let i = 0; i < 12; i++) {
      const endSaturday = new Date(lastSaturday);
      endSaturday.setDate(lastSaturday.getDate() - (i * 7));
      const startSaturday = new Date(endSaturday);
      startSaturday.setDate(endSaturday.getDate() - 7);

      weeks.push({
        value: `${format(startSaturday, "yyyy-MM-dd")}|${format(endSaturday, "yyyy-MM-dd")}`,
        label: `${format(startSaturday, "MMM d")} - ${format(endSaturday, "MMM d, yyyy")}`
      });
    }
    return weeks;
  };

  // Quick week export handler
  const handleQuickWeekExport = async () => {
    if (!quickExportWeek) {
      toast.error("Please select a week");
      return;
    }

    setQuickExportLoading(true);
    try {
      const [startDate, endDate] = quickExportWeek.split("|");

      // Fetch audit data for the week
      const params = new URLSearchParams();
      params.append("start_date", startDate);
      params.append("end_date", endDate);

      const response = await axios.get(`${API}/units/audit/all?${params}`, { withCredentials: true });
      const weekData = response.data || [];

      // Extract unique units for each category
      const respondedUnits = new Set();
      const patrolUnits = new Set();
      const dispatchUnits = new Set();

      weekData.forEach(unit => {
        // Units that responded to incidents or live calls
        if ((unit.incidents_responded?.length > 0) || (unit.live_calls?.length > 0)) {
          respondedUnits.add(unit.unit_name);
        }
        // Units that did patrol
        if (unit.patrol_sessions?.length > 0) {
          patrolUnits.add(unit.unit_name);
        }
        // Dispatch units (check if unit is a dispatcher and had activity)
        if (unit.is_dispatcher && ((unit.incidents_responded?.length > 0) || (unit.live_calls?.length > 0) || (unit.incidents_investigating?.length > 0))) {
          dispatchUnits.add(unit.unit_name);
        }
      });

      // Sort units numerically
      const sortUnits = (a, b) => {
        const numA = parseInt((a.match(/\d+/) || [0])[0]);
        const numB = parseInt((b.match(/\d+/) || [0])[0]);
        if (numA !== numB) return numA - numB;
        return a.localeCompare(b);
      };

      const respondedList = Array.from(respondedUnits).sort(sortUnits);
      const patrolList = Array.from(patrolUnits).sort(sortUnits);
      const dispatchList = Array.from(dispatchUnits).sort(sortUnits);

      // Create CSV content
      const weekLabel = getWeekOptions().find(w => w.value === quickExportWeek)?.label || "";
      const csvLines = [
        `Week: ${weekLabel}`,
        ``,
        `Responded to Calls (${respondedList.length}):,${respondedList.join(",")}`,
        `Patrol Sessions (${patrolList.length}):,${patrolList.join(",")}`,
        `Dispatch Units (${dispatchList.length}):,${dispatchList.join(",")}`
      ];

      const csvContent = csvLines.join("\n");

      // Download the CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `weekly_units_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported: ${respondedList.length} responded, ${patrolList.length} patrol, ${dispatchList.length} dispatch`);
      setShowQuickExportDialog(false);
    } catch (error) {
      console.error("Quick export failed:", error);
      toast.error("Export failed");
    } finally {
      setQuickExportLoading(false);
    }
  };

  // Plain text week export (copy to clipboard)
  const handleQuickWeekTextExport = async () => {
    if (!quickExportWeek) { toast.error("Please select a week"); return; }

    setQuickExportLoading(true);
    try {
      const [startDate, endDate] = quickExportWeek.split("|");
      const params = new URLSearchParams();
      params.append("start_date", startDate);
      params.append("end_date", endDate);

      // Fetch audit data for units + all incidents for accurate arrest count
      const [auditResponse, incidentsResponse] = await Promise.all([
        axios.get(`${API}/units/audit/all?${params}`, { withCredentials: true }),
        axios.get(`${API}/incidents?start_date=${startDate}&end_date=${endDate}&limit=500`, { withCredentials: true })
      ]);
      const weekData = auditResponse.data || [];
      const allIncidents = Array.isArray(incidentsResponse.data) ? incidentsResponse.data : [];

      const respondedUnits = new Set();
      weekData.forEach(unit => {
        if ((unit.incidents_responded?.length > 0) || (unit.live_calls?.length > 0)) {
          respondedUnits.add(unit.unit_name);
        }
      });

      // Count perp arrests from all incidents in date range (same as dashboard arrest card)
      const perpArrestCount = allIncidents.reduce((total, inc) =>
        total + (inc.perp_descriptions || []).filter(p => p.was_arrested).length
      , 0);

      const sortUnits = (a, b) => {
        const numA = parseInt((a.match(/\d+/) || [0])[0]);
        const numB = parseInt((b.match(/\d+/) || [0])[0]);
        if (numA !== numB) return numA - numB;
        return a.localeCompare(b);
      };

      const respondedList = Array.from(respondedUnits).sort(sortUnits);
      // Strip common prefix (e.g. "CH" from "CH27") for cleaner display
      const shortNames = respondedList.map(u => u.replace(/^[A-Z]{2,3}-?/, ""));

      const arrestText = perpArrestCount > 0 ? ` resulting in ${perpArrestCount} arrest${perpArrestCount !== 1 ? "s" : ""}` : "";
      const text = `Thank you to all units who responded to calls the past week${arrestText}:\n\n${shortNames.join(", ")},\n\n\n*Your commitment is valued and appreciated.*`;

      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
      setShowQuickExportDialog(false);
    } catch (error) {
      console.error("Text export failed:", error);
      toast.error("Export failed");
    } finally {
      setQuickExportLoading(false);
    }
  };

  // Client-side filtering of audit data based on activity type and search terms
  const filteredAuditData = useMemo(() => {
    if (!auditData.length) return [];
    
    const incFilterActive = auditDispatcherFilter || auditHasAnyProfile || auditSpecificProfile || auditReadyForArrest || auditProfileArrested || auditArrests || auditHateCrime || auditWeapon || auditInjuries || auditMinorElderly || auditPdActivated || auditHotlineCall || auditReportFiled || auditFootageRequested || auditFootageObtained;

    return auditData.map(unit => {
      // Filter incidents based on search terms (client-side refinement)
      let filteredIncidents = [...(unit.incidents_responded || [])];
      let filteredInvestigating = [...(unit.incidents_investigating || [])];
      let filteredLiveCalls = [...(unit.live_calls || [])];

      // Apply search filters client-side for immediate UI feedback
      if (auditIncidentSearch) {
        const search = auditIncidentSearch.toLowerCase();
        filteredIncidents = filteredIncidents.filter(inc => 
          inc.headline?.toLowerCase().includes(search) || 
          inc.incident_id?.toLowerCase().includes(search)
        );
        filteredInvestigating = filteredInvestigating.filter(inc => 
          inc.headline?.toLowerCase().includes(search) || 
          inc.incident_id?.toLowerCase().includes(search)
        );
        filteredLiveCalls = filteredLiveCalls.filter(lc => 
          lc.call_type?.toLowerCase().includes(search) || 
          lc.call_id?.toLowerCase().includes(search)
        );
      }
      
      if (auditAddressSearch) {
        const search = auditAddressSearch.toLowerCase();
        filteredIncidents = filteredIncidents.filter(inc => 
          inc.address?.toLowerCase().includes(search)
        );
        filteredInvestigating = filteredInvestigating.filter(inc => 
          inc.address?.toLowerCase().includes(search)
        );
        filteredLiveCalls = filteredLiveCalls.filter(lc => 
          lc.location?.toLowerCase().includes(search)
        );
      }
      
      if (auditTypeFilter !== "all") {
        if (auditTypeFilter === "patrol") {
          // When "Patrol Sessions" is selected as type, clear non-patrol activities
          filteredIncidents = [];
          filteredInvestigating = [];
          filteredLiveCalls = [];
        } else {
          // For other types, filter incidents/calls by type
          filteredIncidents = filteredIncidents.filter(inc => inc.incident_type === auditTypeFilter);
          filteredInvestigating = filteredInvestigating.filter(inc => inc.incident_type === auditTypeFilter);
          filteredLiveCalls = filteredLiveCalls.filter(lc => lc.call_type === auditTypeFilter);
        }
      }
      
      // Apply incident-level filters (only to incidents, not live calls)
      if (incFilterActive) {
        const filterInc = (inc) => {
          if (auditDispatcherFilter && !inc.dispatcher_units?.includes(auditDispatcherFilter)) return false;
          if (auditHasAnyProfile) {
            const hasProfile = inc.perp_descriptions?.some(p => p.linked_profile_id) || inc.linked_profiles?.length > 0;
            if (!hasProfile) return false;
          }
          if (auditSpecificProfile) {
            const hasSpecific = inc.perp_descriptions?.some(p => p.linked_profile_id === auditSpecificProfile) || inc.linked_profiles?.includes(auditSpecificProfile);
            if (!hasSpecific) return false;
          }
          if (auditReadyForArrest) {
            if (!inc.perp_descriptions?.some(p => p.identified_for_arrest)) return false;
          }
          if (auditProfileArrested) {
            const pids = [...new Set([
              ...(inc.perp_descriptions?.filter(p => p.linked_profile_id).map(p => p.linked_profile_id) || []),
              ...(inc.linked_profiles || [])
            ])];
            if (auditSpecificProfile) {
              const prof = profiles.find(p => p.profile_id === auditSpecificProfile);
              if (!(prof?.linked_arrests?.length > 0 || prof?.arrests?.length > 0)) return false;
            } else {
              const hasArrested = pids.some(pid => {
                const prof = profiles.find(p => p.profile_id === pid);
                return prof?.linked_arrests?.length > 0 || prof?.arrests?.length > 0;
              });
              if (!hasArrested) return false;
            }
          }
          if (auditArrests) {
            const hasArrest = inc.is_arrest || inc.status === "arrest" || inc.perp_descriptions?.some(p => p.was_arrested);
            if (!hasArrest) return false;
          }
          if (auditHateCrime && !inc.is_hate_crime) return false;
          if (auditWeapon && !inc.weapon_displayed) return false;
          if (auditInjuries) {
            const hasInjuries = inc.injuries_reported || inc.victims?.some(v => v.has_injuries);
            if (!hasInjuries) return false;
          }
          if (auditMinorElderly) {
            if (!inc.victims?.some(v => v.is_minor_or_elderly)) return false;
          }
          if (auditPdActivated && !inc.pd_activated) return false;
          if (auditHotlineCall && !inc.is_hotline_call) return false;
          if (auditReportFiled) {
            const hasReport = inc.police_report_filed || inc.victims?.some(v => v.on_police_report) || inc.witnesses?.some(w => w.on_police_report);
            if (!hasReport) return false;
          }
          if (auditFootageRequested && !inc.footage_requested) return false;
          if (auditFootageObtained && !inc.footage_obtained) return false;
          return true;
        };
        filteredIncidents = filteredIncidents.filter(filterInc);
        filteredInvestigating = filteredInvestigating.filter(filterInc);
      }

      // Apply activity type filter
      if (auditActivityFilter === "incidents") {
        filteredLiveCalls = [];
      } else if (auditActivityFilter === "live_calls") {
        filteredIncidents = [];
        filteredInvestigating = [];
      } else if (auditActivityFilter === "patrol") {
        filteredIncidents = [];
        filteredInvestigating = [];
        filteredLiveCalls = [];
      }

      // Get patrol sessions if needed
      let filteredPatrolSessions = [];
      if ((auditActivityFilter === "both" || auditActivityFilter === "patrol") &&
          (auditTypeFilter === "all" || auditTypeFilter === "patrol")) {
        filteredPatrolSessions = [...(unit.patrol_sessions || [])];
      }

      const totalCount = filteredIncidents.length + filteredInvestigating.length + filteredLiveCalls.length + filteredPatrolSessions.length;

      return {
        ...unit,
        incidents_responded: filteredIncidents,
        incidents_investigating: filteredInvestigating,
        live_calls: filteredLiveCalls,
        patrol_sessions: filteredPatrolSessions,
        total_filtered: totalCount
      };
    }).filter(unit => {
      // Filter out units with no matching results (unless showing all)
      if (auditUnitFilter !== "all") return true;
      return unit.total_filtered > 0 || (!auditIncidentSearch && !auditAddressSearch && auditTypeFilter === "all" && !incFilterActive);
    });
  }, [auditData, auditIncidentSearch, auditAddressSearch, auditTypeFilter, auditActivityFilter, auditUnitFilter, auditDispatcherFilter, auditHasAnyProfile, auditSpecificProfile, auditReadyForArrest, auditProfileArrested, profiles, auditArrests, auditHateCrime, auditWeapon, auditInjuries, auditMinorElderly, auditPdActivated, auditHotlineCall, auditReportFiled, auditFootageRequested, auditFootageObtained]);

  // Generate list format export (matrix: units as rows, incidents as columns)
  const handleListFormatExport = () => {
    if (!filteredAuditData.length) {
      toast.info("No data to export");
      return;
    }
    
    // Helper to format date for export
    const formatExportDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return format(date, 'MM/dd/yy HH:mm');
      } catch {
        return '';
      }
    };
    
    // For patrol-only filter, show session summary instead of individual sessions
    if (auditActivityFilter === "patrol") {
      // Build patrol summary CSV
      const headers = ['Unit', 'Total Sessions', 'Total Minutes', 'Total Hours'];
      const csvRows = [headers.join(',')];
      
      filteredAuditData.forEach(unit => {
        const sessionCount = unit.patrol_sessions?.length || 0;
        const totalMinutes = unit.patrol_sessions?.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) || 0;
        const totalHours = (totalMinutes / 60).toFixed(2);
        
        const row = [
          `"${unit.unit_name}"`,
          sessionCount,
          totalMinutes,
          totalHours
        ];
        csvRows.push(row.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      setExportData(csvContent);
      setExportFilename(`units_patrol_summary_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl("");
      setShowExportDialog(true);
      setShowAuditExportDialog(false);
      return;
    }
    
    // Collect all unique incidents/calls/patrol sessions
    const allActivities = new Map();
    filteredAuditData.forEach(unit => {
      unit.incidents_responded?.forEach(inc => {
        if (!allActivities.has(inc.incident_id)) {
          allActivities.set(inc.incident_id, { id: inc.incident_id, headline: inc.headline, type: 'incident', date: inc.date_time, incidentType: inc.incident_type });
        }
      });
      unit.incidents_investigating?.forEach(inc => {
        if (!allActivities.has(inc.incident_id)) {
          allActivities.set(inc.incident_id, { id: inc.incident_id, headline: inc.headline, type: 'incident', date: inc.date_time, incidentType: inc.incident_type });
        }
      });
      unit.live_calls?.forEach(lc => {
        if (!allActivities.has(lc.call_id)) {
          allActivities.set(lc.call_id, { id: lc.call_id, headline: lc.call_type, type: 'live_call', date: lc.created_at, incidentType: lc.call_type });
        }
      });
      // Add patrol sessions
      unit.patrol_sessions?.forEach(session => {
        const sessionKey = `patrol_${session.session_id || session.session_start}`;
        if (!allActivities.has(sessionKey)) {
          allActivities.set(sessionKey, { 
            id: sessionKey, 
            headline: `Patrol - ${session.car_name || 'Unknown'}`, 
            type: 'patrol', 
            date: session.session_end || session.session_start,
            incidentType: 'Patrol',
            sessionStart: session.session_start,
            sessionEnd: session.session_end,
            duration: session.duration_minutes
          });
        }
      });
    });
    
    // Sort activities by date
    const sortedActivities = Array.from(allActivities.values()).sort((a, b) => 
      new Date(b.date || 0) - new Date(a.date || 0)
    );
    
    // Build CSV header with date included
    // Format: "ID (Date - Type - Headline)"
    // Add patrol session count column if showing all activities
    const includesPatrol = auditActivityFilter === "both";
    const headers = includesPatrol 
      ? ['Unit', 'Patrol Sessions', ...sortedActivities.map(a => {
          const dateStr = formatExportDate(a.date);
          const typeLabel = a.type === 'live_call' ? 'LC' : a.type === 'patrol' ? 'PTL' : 'INC';
          const headline = a.headline || 'N/A';
          return `"${a.id} (${dateStr} - ${typeLabel} - ${headline})"`;
        })]
      : ['Unit', ...sortedActivities.map(a => {
          const dateStr = formatExportDate(a.date);
          const typeLabel = a.type === 'live_call' ? 'LC' : a.type === 'patrol' ? 'PTL' : 'INC';
          const headline = a.headline || 'N/A';
          return `"${a.id} (${dateStr} - ${typeLabel} - ${headline})"`;
        })];
    const csvRows = [headers.join(',')];
    
    // Build data rows
    filteredAuditData.forEach(unit => {
      const respondedIds = new Set(unit.incidents_responded?.map(i => i.incident_id) || []);
      const investigatingIds = new Set(unit.incidents_investigating?.map(i => i.incident_id) || []);
      const liveCallIds = new Set(unit.live_calls?.map(l => l.call_id) || []);
      const patrolSessionKeys = new Set(unit.patrol_sessions?.map(s => `patrol_${s.session_id || s.session_start}`) || []);
      const patrolSessionCount = unit.patrol_sessions?.length || 0;
      
      const row = includesPatrol 
        ? [`"${unit.unit_name}"`, patrolSessionCount]
        : [`"${unit.unit_name}"`];
      
      sortedActivities.forEach(activity => {
        let value = '';
        if (activity.type === 'incident') {
          if (respondedIds.has(activity.id) && investigatingIds.has(activity.id)) {
            value = 'R+I';
          } else if (respondedIds.has(activity.id)) {
            value = 'R';
          } else if (investigatingIds.has(activity.id)) {
            value = 'I';
          }
        } else if (activity.type === 'patrol') {
          if (patrolSessionKeys.has(activity.id)) {
            value = `${activity.duration || 0}min`;
          }
        } else {
          if (liveCallIds.has(activity.id)) {
            value = 'X';
          }
        }
        row.push(`"${value}"`);
      });
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    setExportData(csvContent);
    setExportFilename(`units_audit_list_format_${format(new Date(), "yyyy-MM-dd")}.csv`);
    setExportDownloadUrl("");
    setShowExportDialog(true);
    setShowAuditExportDialog(false);
  };

  // Standard export
  const handleStandardExport = async () => {
    setShowAuditExportDialog(false);
    await handleAuditExport();
  };

  const filteredUnits = units.filter(unit => unit.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredDeactivatedUnits = deactivatedUnits.filter(unit => unit.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)]" data-testid="units-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-blue-500" />
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Units</h1>
          <Badge variant="outline">{units.length} Active</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-800 rounded p-0.5 mr-2">
            <Button variant={mainTab === "units" ? "default" : "ghost"} size="sm" onClick={() => setMainTab("units")} className={mainTab === "units" ? "bg-blue-600" : ""}>
              <Users className="w-4 h-4 mr-1" />Units
            </Button>
            <Button variant={mainTab === "audit" ? "default" : "ghost"} size="sm" onClick={() => { setMainTab("audit"); if (auditData.length === 0) fetchAuditData(); }} className={mainTab === "audit" ? "bg-blue-600" : ""}>
              <Activity className="w-4 h-4 mr-1" />Audit
            </Button>
            <Button variant={mainTab === "dispatch" ? "default" : "ghost"} size="sm" onClick={() => setMainTab("dispatch")} className={mainTab === "dispatch" ? "bg-blue-600" : ""}>
              <Phone className="w-4 h-4 mr-1" />Dispatch
            </Button>
          </div>
          <Dialog open={showPatrolExportDialog} onOpenChange={setShowPatrolExportDialog}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" />Patrol Export</Button></DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle className="uppercase tracking-wider">Export Patrol Data</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start Date</Label><Input type="date" value={patrolExportStartDate} onChange={(e) => setPatrolExportStartDate(e.target.value)} className="input-dark" /></div>
                  <div><Label>End Date</Label><Input type="date" value={patrolExportEndDate} onChange={(e) => setPatrolExportEndDate(e.target.value)} className="input-dark" /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => { handleGlobalPatrolExport("hours"); setShowPatrolExportDialog(false); }} className="flex-1 btn-primary"><Download className="w-4 h-4 mr-2" />Patrol Hours</Button>
                  <Button onClick={() => { handleGlobalPatrolExport("logs"); setShowPatrolExportDialog(false); }} className="flex-1" variant="outline"><Download className="w-4 h-4 mr-2" />Patrol Logs</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showMasterExport} onOpenChange={setShowMasterExport}>
            <DialogTrigger asChild><Button variant="outline"><Download className="w-4 h-4 mr-1" />Export All</Button></DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader><DialogTitle className="uppercase tracking-wider">Export Units Report</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start Date</Label><Input type="date" value={masterExportStartDate} onChange={(e) => setMasterExportStartDate(e.target.value)} className="input-dark" /></div>
                  <div><Label>End Date</Label><Input type="date" value={masterExportEndDate} onChange={(e) => setMasterExportEndDate(e.target.value)} className="input-dark" /></div>
                </div>
                <Button onClick={handleMasterExport} className="w-full btn-primary"><Download className="w-4 h-4 mr-2" />Generate Report</Button>
              </div>
            </DialogContent>
          </Dialog>
          {user?.role === "admin" && (
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-1" />Add Unit</Button></DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800">
                <DialogHeader><DialogTitle className="uppercase tracking-wider">Add New Unit</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><Label>Unit Name (e.g., CH99)</Label><Input value={newUnit.name} onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value.toUpperCase() })} placeholder="CH..." className="input-dark" /></div>
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2"><Checkbox id="is_dispatcher" checked={newUnit.is_dispatcher} onCheckedChange={(checked) => setNewUnit({ ...newUnit, is_dispatcher: checked })} /><Label htmlFor="is_dispatcher">Dispatcher</Label></div>
                    <div className="flex items-center gap-2"><Checkbox id="is_investigator" checked={newUnit.is_investigator} onCheckedChange={(checked) => setNewUnit({ ...newUnit, is_investigator: checked })} /><Label htmlFor="is_investigator">Investigator</Label></div>
                    <div className="flex items-center gap-2"><Checkbox id="is_patrol" checked={newUnit.is_patrol} onCheckedChange={(checked) => setNewUnit({ ...newUnit, is_patrol: checked })} /><Label htmlFor="is_patrol">Patrol</Label></div>
                  </div>
                  <Button onClick={handleCreateUnit} className="w-full btn-primary">Create Unit</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Main Content */}
      {mainTab === "dispatch" ? (
        <Dispatch embedded />
      ) : mainTab === "units" ? (
        <div className="flex flex-col lg:flex-row h-full gap-4">
          {/* Left Panel - Unit List */}
          <div className={`w-full lg:w-1/3 bg-slate-900 border border-slate-800 rounded-sm flex flex-col ${selectedUnit ? 'hidden lg:flex' : ''}`}>
            <div className="p-3 border-b border-slate-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search units..." className="pl-10 input-dark" />
              </div>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="grid grid-cols-2 mx-2 mt-2 bg-slate-950">
                <TabsTrigger value="active">Active ({filteredUnits.length})</TabsTrigger>
                <TabsTrigger value="deactivated">Deactivated ({filteredDeactivatedUnits.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full p-2">
                  {filteredUnits.map((unit) => (
                    <div key={unit.unit_id} onClick={() => handleSelectUnit(unit)} className={`p-3 rounded-sm mb-1 cursor-pointer flex items-center justify-between group ${selectedUnit?.unit_id === unit.unit_id ? "bg-blue-600/20 border border-blue-500/50" : "hover:bg-slate-800"}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-lg">{unit.name}</span>
                        <div className="flex gap-1">
                          {unit.is_dispatcher && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500"><Radio className="w-3 h-3 mr-1" />DISP</Badge>}
                          {unit.is_investigator && <Badge variant="outline" className="text-xs text-blue-400 border-blue-500"><Shield className="w-3 h-3 mr-1" />INV</Badge>}
                          {unit.is_patrol && <Badge variant="outline" className="text-xs text-green-400 border-green-500"><Car className="w-3 h-3 mr-1" />PTL</Badge>}
                          {hasActivePlaque(unit) && <Badge variant="outline" className="text-xs text-purple-400 border-purple-500"><Award className="w-3 h-3 mr-1" />PLQ</Badge>}
                        </div>
                      </div>
                      {user?.role === "admin" && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditUnit(unit); setShowEditDialog(true); }}><Edit className="w-4 h-4 text-blue-400" /></Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeactivateUnit(unit.unit_id, unit.name); }}><Power className="w-4 h-4 text-amber-500" /></Button>
                        </div>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              </TabsContent>
              <TabsContent value="deactivated" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full p-2">
                  {filteredDeactivatedUnits.length === 0 ? <p className="text-center text-slate-500 py-8">No deactivated units</p> : filteredDeactivatedUnits.map((unit) => (
                    <div key={unit.unit_id} className="p-3 rounded-sm mb-1 flex items-center justify-between bg-slate-950 opacity-60">
                      <span className="font-mono font-bold text-lg text-slate-500">{unit.name}</span>
                      {user?.role === "admin" && <Button variant="ghost" size="sm" onClick={() => handleReactivateUnit(unit.unit_id)}><RotateCcw className="w-4 h-4 text-green-500" /></Button>}
                    </div>
                  ))}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Unit Details */}
          <div className={`flex-1 bg-slate-900 border border-slate-800 rounded-sm flex flex-col overflow-hidden ${!selectedUnit ? 'hidden lg:flex' : ''}`}>
            {selectedUnit ? (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center gap-4 flex-shrink-0">
                  <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSelectedUnit(null)}><ArrowLeft className="w-4 h-4" /></Button>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold font-mono">{selectedUnit.name}</h2>
                    <div className="flex gap-2 mt-1">
                      {selectedUnit.is_dispatcher && <Badge className="bg-amber-900/50 text-amber-200">Dispatcher</Badge>}
                      {selectedUnit.is_investigator && <Badge className="bg-blue-900/50 text-blue-200">Investigator</Badge>}
                      {selectedUnit.is_patrol && <Badge className="bg-emerald-900/50 text-emerald-200">Patrol</Badge>}
                    </div>
                  </div>
                </div>

                <Tabs value={detailTab} onValueChange={setDetailTab} className="flex-1 flex flex-col overflow-hidden">
                  <TabsList className="bg-slate-950 w-fit mx-4 mt-2">
                    <TabsTrigger value="incidents">Incidents</TabsTrigger>
                    <TabsTrigger value="patrol">Patrol</TabsTrigger>
                    <TabsTrigger value="map"><Navigation className="w-4 h-4 mr-1" />Map</TabsTrigger>
                    <TabsTrigger value="id"><CreditCard className="w-4 h-4 mr-1" />ID</TabsTrigger>
                  </TabsList>

                  {/* Incidents Tab */}
                  <TabsContent value="incidents" className="flex-1 overflow-auto p-4 mt-0">
                    <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-950 rounded border border-slate-800">
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">Start Date</Label><Input type="date" value={incidentStartDate} onChange={(e) => setIncidentStartDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">End Date</Label><Input type="date" value={incidentEndDate} onChange={(e) => setIncidentEndDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs text-slate-400">Incident Type</Label>
                        <Select value={incidentTypeFilter} onValueChange={setIncidentTypeFilter}>
                          <SelectTrigger className="input-dark h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {incidentTypes.map(t => <SelectItem key={t.type_id || t.name} value={t.name}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end"><Button size="sm" variant="outline" onClick={() => { setIncidentStartDate(""); setIncidentEndDate(""); setIncidentTypeFilter("all"); }}><X className="w-3 h-3" /></Button></div>
                    </div>
                    <div className="flex gap-2 flex-wrap mb-4">
                      <Button variant="outline" size="sm" onClick={() => handleExportFiltered("responded")}><Download className="w-4 h-4 mr-1" />Export Responded ({filteredRespondedIncidents.length})</Button>
                      <Button variant="outline" size="sm" onClick={() => handleExportFiltered("investigating")}><Download className="w-4 h-4 mr-1" />Export Investigating ({filteredInvestigatingIncidents.length})</Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-2xl font-bold text-blue-400">{filteredRespondedIncidents.length}</p><p className="text-xs text-slate-500 uppercase">Responded</p></div><FileText className="w-6 h-6 text-blue-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-2xl font-bold text-amber-400">{filteredInvestigatingIncidents.length}</p><p className="text-xs text-slate-500 uppercase">Investigating</p></div><Shield className="w-6 h-6 text-amber-500/30" /></CardContent></Card>
                    </div>

                    {/* Year-average comparison — same below/near/above bands as the
                        Dashboard "Unit Call Average" card, but for a single unit
                        against any chosen calendar year. */}
                    <Card className="bg-slate-950 border-slate-800 mb-4">
                      <CardContent className="p-3">
                        {(() => {
                          const units = yearStats?.units || [];
                          const withCalls = units.filter(u => u.incidents > 0);
                          const total = withCalls.reduce((s, u) => s + u.incidents, 0);
                          const avg = withCalls.length ? total / withCalls.length : 0;
                          const mine = (units.find(u => u.unit === selectedUnit.name)?.incidents) || 0;
                          const threshold = Math.max(1, avg * 0.15);
                          let bandLabel = "near average", bandColor = "text-amber-400", bandBg = "border-amber-900/30 bg-amber-950/20";
                          if (avg <= 0) { bandLabel = "no data", bandColor = "text-slate-500"; bandBg = "border-slate-800"; }
                          else if (mine < avg - threshold) { bandLabel = "below average"; bandColor = "text-red-400"; bandBg = "border-red-900/30 bg-red-950/20"; }
                          else if (mine > avg + threshold) { bandLabel = "above average"; bandColor = "text-green-400"; bandBg = "border-green-900/30 bg-green-950/20"; }
                          const diff = mine - avg;
                          const sign = diff >= 0 ? "+" : "";
                          const years = (yearStats?.available_years && yearStats.available_years.length) ? yearStats.available_years : [yearStatsYear];
                          return (
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="flex items-center gap-2">
                                <ArrowUpDown className="w-4 h-4 text-blue-400" />
                                <span className="text-xs uppercase tracking-wider text-slate-400">Year Avg</span>
                              </div>
                              <Select value={String(yearStatsYear)} onValueChange={(v) => setYearStatsYear(parseInt(v, 10))}>
                                <SelectTrigger className="input-dark h-8 w-[110px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              {yearStatsLoading ? (
                                <span className="text-xs text-slate-500">Loading…</span>
                              ) : (
                                <div className={`flex items-center gap-3 px-3 py-1.5 rounded border ${bandBg}`}>
                                  <span className={`text-xs font-semibold uppercase tracking-wider ${bandColor}`}>{bandLabel}</span>
                                  <span className="text-sm">
                                    <span className="font-bold text-slate-100">{mine}</span>
                                    <span className="text-slate-500"> / avg {avg.toFixed(1)}</span>
                                  </span>
                                  {avg > 0 && (
                                    <span className={`text-[11px] font-mono ${bandColor}`}>{sign}{diff.toFixed(1)}</span>
                                  )}
                                </div>
                              )}
                              {avg > 0 && (
                                <span className="text-[10px] text-slate-600 ml-auto">
                                  {total} incidents · {withCalls.length} active units
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><h3 className="text-xs uppercase text-slate-500 mb-2">Responded</h3><Card className="bg-slate-950 border-slate-800"><ScrollArea className="h-64"><CardContent className="p-2">{filteredRespondedIncidents.length === 0 ? <p className="text-center text-slate-500 py-8">No incidents</p> : filteredRespondedIncidents.map(inc => <div key={inc.incident_id} className="p-2 border-b border-slate-800 last:border-0 cursor-pointer hover:bg-slate-800/50" onClick={() => navigate(`/incidents/${inc.incident_id}`)}><p className="text-sm font-medium truncate">{inc.headline}</p><div className="flex items-center gap-2 mt-1"><Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge><span className="text-xs text-slate-500">{inc.date_time ? format(new Date(inc.date_time), "MMM d, yyyy") : ""}</span></div></div>)}</CardContent></ScrollArea></Card></div>
                      <div><h3 className="text-xs uppercase text-slate-500 mb-2">Investigating</h3><Card className="bg-slate-950 border-slate-800"><ScrollArea className="h-64"><CardContent className="p-2">{filteredInvestigatingIncidents.length === 0 ? <p className="text-center text-slate-500 py-8">No cases</p> : filteredInvestigatingIncidents.map(inc => <div key={inc.incident_id} className="p-2 border-b border-slate-800 last:border-0 cursor-pointer hover:bg-slate-800/50" onClick={() => navigate(`/incidents/${inc.incident_id}`)}><p className="text-sm font-medium truncate">{inc.headline}</p><div className="flex items-center gap-2 mt-1"><Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge><span className="text-xs text-slate-500">{inc.date_time ? format(new Date(inc.date_time), "MMM d, yyyy") : ""}</span></div></div>)}</CardContent></ScrollArea></Card></div>
                    </div>
                  </TabsContent>

                  {/* Patrol Tab */}
                  <TabsContent value="patrol" className="flex-1 overflow-auto p-4 mt-0">
                    <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-950 rounded border border-slate-800">
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">Start Date</Label><Input type="date" value={patrolStartDate} onChange={(e) => setPatrolStartDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">End Date</Label><Input type="date" value={patrolEndDate} onChange={(e) => setPatrolEndDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex items-end"><Button size="sm" variant="outline" onClick={() => { setPatrolStartDate(""); setPatrolEndDate(""); }}><X className="w-3 h-3" /></Button></div>
                    </div>
                    <div className="flex gap-2 flex-wrap mb-4">
                      <Button variant="outline" size="sm" onClick={() => handleExportFiltered("patrol")}><Download className="w-4 h-4 mr-1" />Export Filtered ({filteredPatrolLogs.length})</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-emerald-400">{unitPatrolStats?.total_patrol_hours?.toFixed(1) || "0"}h</p><p className="text-xs text-slate-500 uppercase">Total Hours</p></div><Clock className="w-6 h-6 text-emerald-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-blue-400">{unitPatrolStats?.total_sessions || "0"}</p><p className="text-xs text-slate-500 uppercase">Sessions</p></div><Car className="w-6 h-6 text-blue-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-amber-400">{unitPatrolStats?.average_session_minutes ? (unitPatrolStats.average_session_minutes / 60).toFixed(1) : "0"}h</p><p className="text-xs text-slate-500 uppercase">Avg Session</p></div><Clock className="w-6 h-6 text-amber-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-purple-400">{filteredPatrolLogs.length}</p><p className="text-xs text-slate-500 uppercase">Log Entries</p></div><FileText className="w-6 h-6 text-purple-500/30" /></CardContent></Card>
                    </div>
                    {/* Sub-view toggle: original per-event log list vs paired sessions */}
                    <div className="flex gap-1 mb-3 w-fit p-0.5 bg-slate-950 rounded border border-slate-800">
                      <button
                        type="button"
                        onClick={() => setPatrolView("logs")}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${patrolView === "logs" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
                      >
                        Log Entries ({filteredPatrolLogs.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setPatrolView("sessions")}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${patrolView === "sessions" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
                      >
                        Sessions ({filteredPatrolSessions.length})
                      </button>
                    </div>
                    {patrolView === "logs" ? (
                    <Card className="bg-slate-950 border-slate-800"><ScrollArea className="h-[300px]"><CardContent className="p-2">{filteredPatrolLogs.length === 0 ? <p className="text-center text-slate-500 py-8">No patrol activity</p> : <div className="space-y-2">{filteredPatrolLogs.map(log => <div key={log.log_id} className="p-3 bg-slate-900 rounded border border-slate-800"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Badge variant="outline" className="font-mono">{log.car_name}</Badge><span className="text-slate-400">{log.action === "login" && "logged in"}{log.action === "logout" && "logged out"}{log.action === "join" && "joined"}{log.action === "leave" && "left"}</span></div><span className="text-xs text-slate-500">{format(new Date(log.timestamp), "MMM d, h:mm a")}</span></div>{log.parked_location && <p className="text-sm text-slate-400 mt-1"><MapPin className="w-3 h-3 inline mr-1" />{log.parked_location}</p>}{log.session_duration_minutes && <p className="text-sm text-emerald-400 mt-1"><Clock className="w-3 h-3 inline mr-1" />{Math.floor(log.session_duration_minutes / 60)}h {log.session_duration_minutes % 60}m</p>}</div>)}</div>}</CardContent></ScrollArea></Card>
                    ) : (
                    <Card className="bg-slate-950 border-slate-800"><ScrollArea className="h-[300px]"><CardContent className="p-2">{filteredPatrolSessions.length === 0 ? <p className="text-center text-slate-500 py-8">No patrol sessions</p> : <div className="space-y-2">{filteredPatrolSessions.map(s => (
                      <div key={s.key} className="p-3 bg-slate-900 rounded border border-slate-800">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="font-mono">{s.car_name}</Badge>
                            {s.ongoing
                              ? <Badge className="bg-emerald-900/50 text-emerald-300 text-[10px]">Active</Badge>
                              : <span className="text-sm text-emerald-400"><Clock className="w-3 h-3 inline mr-1" />{Math.floor(s.duration / 60)}h {s.duration % 60}m</span>}
                          </div>
                          <span className="text-xs text-slate-500">{format(new Date(s.end || s.start), "MMM d")}</span>
                        </div>
                        <div className="mt-1.5 space-y-0.5 text-sm">
                          <p className="text-slate-400"><span className="text-emerald-500 font-medium">▸ In:</span> {s.start ? format(new Date(s.start), "MMM d, h:mm a") : "—"}{s.login_by && <span className="text-slate-500"> · {s.login_by}</span>}</p>
                          <p className="text-slate-400"><span className="text-red-500 font-medium">◂ Out:</span> {s.end ? <>{format(new Date(s.end), "MMM d, h:mm a")}{s.logout_by && <span className="text-slate-500"> · {s.logout_by}</span>}</> : <span className="text-emerald-400">Still on patrol</span>}</p>
                        </div>
                        {s.parked_location && <p className="text-sm text-slate-400 mt-1"><MapPin className="w-3 h-3 inline mr-1" />{s.parked_location}</p>}
                        {s.fuel_level && <p className="text-xs text-slate-500 mt-1">Fuel: {s.fuel_level}</p>}
                        {s.logout_note && <p className="text-xs text-slate-500 mt-1 italic">"{s.logout_note}"</p>}
                      </div>
                    ))}</div>}</CardContent></ScrollArea></Card>
                    )}
                  </TabsContent>

                  {/* Map/Location History Tab */}
                  <TabsContent value="map" className="flex-1 overflow-auto p-4 mt-0">
                    <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-950 rounded border border-slate-800">
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">Start Date</Label><Input type="date" value={locationStartDate} onChange={(e) => setLocationStartDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex-1 min-w-[120px]"><Label className="text-xs text-slate-400">End Date</Label><Input type="date" value={locationEndDate} onChange={(e) => setLocationEndDate(e.target.value)} className="input-dark h-8" /></div>
                      <div className="flex items-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setLocationStartDate(""); setLocationEndDate(""); }}><X className="w-3 h-3" /></Button>
                        <Button size="sm" onClick={() => fetchLocationHistory(selectedUnit.name, locationStartDate, locationEndDate)}><Filter className="w-3 h-3 mr-1" />Apply</Button>
                      </div>
                    </div>
                    <div className="flex gap-2 mb-4"><Button variant="outline" size="sm" onClick={() => handleExportFiltered("location")}><Download className="w-4 h-4 mr-1" />Export Location History ({filteredLocationHistory.length})</Button></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-green-400">{filteredLocationHistory.length}</p><p className="text-xs text-slate-500 uppercase">Total Shares</p></div><Navigation className="w-6 h-6 text-green-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-blue-400">{[...new Set(filteredLocationHistory.map(l => l.live_call_id).filter(Boolean))].length}</p><p className="text-xs text-slate-500 uppercase">Calls</p></div><Radio className="w-6 h-6 text-blue-500/30" /></CardContent></Card>
                      <Card className="bg-slate-950 border-slate-800"><CardContent className="p-3 flex items-center justify-between"><div><p className="text-xl font-bold text-purple-400">{filteredLocationHistory.length > 0 ? format(new Date(filteredLocationHistory[0].timestamp), "MMM d") : "-"}</p><p className="text-xs text-slate-500 uppercase">Last Share</p></div><Clock className="w-6 h-6 text-purple-500/30" /></CardContent></Card>
                    </div>
                    <Card className="bg-slate-950 border-slate-800"><ScrollArea className="h-[350px]"><CardContent className="p-2">{locationLoading ? <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div></div> : filteredLocationHistory.length === 0 ? <p className="text-center text-slate-500 py-8">No location history</p> : <div className="space-y-2">{filteredLocationHistory.map((loc, idx) => <div key={idx} className="p-3 bg-slate-900 rounded border border-slate-800"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Navigation className="w-4 h-4 text-green-400" /><span className="text-sm font-mono">{loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}</span>{loc.accuracy && <Badge variant="outline" className="text-[10px]">±{Math.round(loc.accuracy)}m</Badge>}</div><span className="text-xs text-slate-500">{format(new Date(loc.timestamp), "MMM d, h:mm:ss a")}</span></div>{loc.live_call_id && <p className="text-xs text-blue-400 mt-1"><Radio className="w-3 h-3 inline mr-1" />Call: {loc.live_call_id}</p>}<a href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:underline mt-1 inline-block"><ExternalLink className="w-3 h-3 inline mr-1" />View on Google Maps</a></div>)}</div>}</CardContent></ScrollArea></Card>
                  </TabsContent>

                  {/* ID & Plaques Tab */}
                  <TabsContent value="id" className="flex-1 overflow-auto p-4 mt-0 space-y-6">
                    {/* ID Cards */}
                    <Card className="bg-slate-950 border-slate-800">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-200 flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-blue-400" />ID Cards
                          </h3>
                          <Button size="sm" onClick={openNewCreditCard}><Plus className="w-3.5 h-3.5 mr-1" />Issue ID Card</Button>
                        </div>
                        {(selectedUnit.id_cards || []).length === 0 ? (
                          <p className="text-center text-slate-500 py-6 text-sm">No ID cards on file.</p>
                        ) : (
                          <div className="space-y-2">
                            {(selectedUnit.id_cards || []).map((rec) => (
                              <div key={rec.record_id} className="p-3 bg-slate-900 rounded border border-slate-800 flex flex-wrap gap-3 items-center">
                                <div className="flex-1 min-w-[220px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-blue-300">{rec.card_number || "(no #)"}</span>
                                    {rec.returned_at ? (
                                      <Badge className="bg-emerald-900/50 text-emerald-200 text-[10px]">Returned {rec.returned_at}</Badge>
                                    ) : rec.expires_at ? (
                                      <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">Active</Badge>
                                    ) : null}
                                    {rec.old_card_returned && <Badge variant="outline" className="text-[10px]">Old returned</Badge>}
                                  </div>
                                  <div className="text-xs text-slate-400 mt-1">
                                    Issued: {rec.issued_at || "—"} · Expires: {rec.expires_at || "—"}
                                  </div>
                                  {rec.notes && <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{rec.notes}</div>}
                                </div>
                                <div className="flex gap-1">
                                  {!rec.returned_at && (
                                    <Button size="sm" variant="outline" onClick={() => markCreditCardReturned(rec)}><Check className="w-3.5 h-3.5 mr-1" />Returned</Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => openEditCreditCard(rec)}><Edit className="w-3.5 h-3.5" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Plaques */}
                    <Card className="bg-slate-950 border-slate-800">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-200 flex items-center gap-2">
                            <Award className="w-4 h-4 text-amber-400" />Plaques
                          </h3>
                          <Button size="sm" onClick={openNewPlaque}><Plus className="w-3.5 h-3.5 mr-1" />Issue Plaque</Button>
                        </div>
                        {(selectedUnit.plaques || []).length === 0 ? (
                          <p className="text-center text-slate-500 py-6 text-sm">No plaques on file.</p>
                        ) : (
                          <div className="space-y-2">
                            {(selectedUnit.plaques || []).map((rec) => (
                              <div key={rec.record_id} className="p-3 bg-slate-900 rounded border border-slate-800 flex flex-wrap gap-3 items-center">
                                <div className="flex-1 min-w-[220px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-amber-300">{rec.plaque_number || "(no #)"}</span>
                                    {rec.returned_at ? (
                                      <Badge className="bg-emerald-900/50 text-emerald-200 text-[10px]">Returned {rec.returned_at}</Badge>
                                    ) : (
                                      <Badge className="bg-amber-900/50 text-amber-200 text-[10px]">Active</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-400 mt-1">
                                    Issued: {rec.issued_at || "—"} · Expires: {rec.expires_at || "—"}
                                  </div>
                                  {rec.notes && <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{rec.notes}</div>}
                                </div>
                                <div className="flex gap-1">
                                  {!rec.returned_at && (
                                    <Button size="sm" variant="outline" onClick={() => markPlaqueReturned(rec)}><Check className="w-3.5 h-3.5 mr-1" />Returned</Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => openEditPlaque(rec)}><Edit className="w-3.5 h-3.5" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <p className="text-xs text-slate-500 italic">Records are deleted only by admins under Settings → Data.</p>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500"><div className="text-center"><Users className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Select a unit to view details</p></div></div>
            )}
          </div>
        </div>
      ) : (
        /* Audit Tab */
        <div className="h-full bg-slate-900 border border-slate-800 rounded-sm flex flex-col">
          <div className="p-4 border-b border-slate-800">
            {/* Row 1: Unit selector and search */}
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div className="w-48">
                <Label className="text-xs text-slate-400 mb-1 block">Select Unit</Label>
                <Select value={auditUnitFilter} onValueChange={setAuditUnitFilter}>
                  <SelectTrigger className="input-dark">
                    <SelectValue placeholder="All Units" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="all">All Units</SelectItem>
                    {units.map(u => (
                      <SelectItem key={u.unit_id} value={u.name}>
                        <span className="font-mono">{u.name}</span>
                        {u.is_dispatcher && <span className="ml-2 text-amber-400 text-xs">DISP</span>}
                        {u.is_investigator && <span className="ml-2 text-blue-400 text-xs">INV</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-slate-400 mb-1 block">Search Headline/ID</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input value={auditIncidentSearch} onChange={(e) => setAuditIncidentSearch(e.target.value)} placeholder="Incident headline or ID..." className="pl-10 input-dark" onKeyDown={(e) => e.key === "Enter" && fetchAuditData()} />
                </div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-slate-400 mb-1 block">Search Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input value={auditAddressSearch} onChange={(e) => setAuditAddressSearch(e.target.value)} placeholder="Street, location..." className="pl-10 input-dark" onKeyDown={(e) => e.key === "Enter" && fetchAuditData()} />
                </div>
              </div>
            </div>
            {/* Row 2: Type filter, activity filter, and dates */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Label className="text-xs text-slate-400 mb-1 block">Incident Type</Label>
                <Select value={auditTypeFilter} onValueChange={setAuditTypeFilter}>
                  <SelectTrigger className="input-dark">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="patrol">Patrol Sessions</SelectItem>
                    {incidentTypes.map(t => <SelectItem key={t.type_id || t.name} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-400 mb-1 block">Activity Type</Label>
                <Select value={auditActivityFilter} onValueChange={setAuditActivityFilter}>
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">All</SelectItem>
                    <SelectItem value="incidents">Incidents Only</SelectItem>
                    <SelectItem value="live_calls">Dispatch Only</SelectItem>
                    <SelectItem value="patrol">Patrol Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="text-xs text-slate-400 mb-1 block">Start Date</Label>
                <Input type="date" value={auditStartDate} onChange={(e) => setAuditStartDate(e.target.value)} className="input-dark" />
              </div>
              <div className="w-32">
                <Label className="text-xs text-slate-400 mb-1 block">End Date</Label>
                <Input type="date" value={auditEndDate} onChange={(e) => setAuditEndDate(e.target.value)} className="input-dark" />
              </div>
              <Button onClick={fetchAuditData} disabled={auditLoading} className="btn-primary">
                <Filter className="w-4 h-4 mr-1" />{auditLoading ? "Loading..." : "Search"}
              </Button>
              <Button variant="outline" onClick={() => { setAuditStartDate(""); setAuditEndDate(""); setAuditIncidentSearch(""); setAuditAddressSearch(""); setAuditTypeFilter("all"); setAuditUnitFilter("all"); setAuditActivityFilter("both"); setAuditDispatcherFilter(""); setAuditHasAnyProfile(false); setAuditSpecificProfile(""); setAuditReadyForArrest(false); setAuditProfileArrested(false); setAuditArrests(false); setAuditHateCrime(false); setAuditWeapon(false); setAuditInjuries(false); setAuditMinorElderly(false); setAuditPdActivated(false); setAuditHotlineCall(false); setAuditReportFiled(false); setAuditFootageRequested(false); setAuditFootageObtained(false); setShowAuditIncidentFilters(false); }}>
                <X className="w-4 h-4 mr-1" />Clear All
              </Button>
              <Button variant="outline" onClick={() => setShowAuditExportDialog(true)} disabled={filteredAuditData.length === 0}>
                <Download className="w-4 h-4 mr-1" />Export
              </Button>
              <Button variant="outline" onClick={() => setShowQuickExportDialog(true)} className="border-amber-600 text-amber-400 hover:bg-amber-900/30">
                <Calendar className="w-4 h-4 mr-1" />Week Export
              </Button>
            </div>
            {/* Incident Filters toggle */}
            <div className="flex items-center gap-2 mt-2">
              <Button variant={showAuditIncidentFilters ? "default" : "outline"} size="sm" onClick={() => setShowAuditIncidentFilters(!showAuditIncidentFilters)} className={showAuditIncidentFilters ? "bg-blue-600" : ""}>
                <Filter className="w-3 h-3 mr-1" />Incident Filters
                {[auditDispatcherFilter, auditHasAnyProfile, auditSpecificProfile, auditReadyForArrest, auditProfileArrested, auditArrests, auditHateCrime, auditWeapon, auditInjuries, auditMinorElderly, auditPdActivated, auditHotlineCall, auditReportFiled, auditFootageRequested, auditFootageObtained].filter(Boolean).length > 0 && (
                  <Badge className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-[9px] bg-blue-600">{[auditDispatcherFilter, auditHasAnyProfile, auditSpecificProfile, auditReadyForArrest, auditProfileArrested, auditArrests, auditHateCrime, auditWeapon, auditInjuries, auditMinorElderly, auditPdActivated, auditHotlineCall, auditReportFiled, auditFootageRequested, auditFootageObtained].filter(Boolean).length}</Badge>
                )}
              </Button>
            </div>
            {showAuditIncidentFilters && (
              <div className="mt-3 space-y-3 bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                {/* Dispatcher + Profile dropdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-400 uppercase tracking-wide mb-2 block">Dispatcher</Label>
                    <Select value={auditDispatcherFilter || "all"} onValueChange={(v) => setAuditDispatcherFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                        <SelectValue placeholder="All Dispatchers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dispatchers</SelectItem>
                        {dispatchers.map((d) => (
                          <SelectItem key={d.unit_id || d.name} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-cyan-950/20 border border-cyan-900/30 rounded-lg p-3">
                    <Label className="text-xs text-cyan-400 uppercase tracking-wide mb-2 block">Profile</Label>
                    <Select value={auditSpecificProfile || "all"} onValueChange={(v) => setAuditSpecificProfile(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-slate-900 border-slate-700">
                        <SelectValue placeholder="Select Profile" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Profiles</SelectItem>
                        {profiles.map((p) => (
                          <SelectItem key={p.profile_id} value={p.profile_id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditHasAnyProfile ? 'bg-cyan-600/30 border border-cyan-500 text-cyan-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                        <Checkbox checked={auditHasAnyProfile} onCheckedChange={(c) => setAuditHasAnyProfile(!!c)} className="hidden" />
                        Has Any Profile
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditReadyForArrest ? 'bg-amber-600/30 border border-amber-500 text-amber-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                        <Checkbox checked={auditReadyForArrest} onCheckedChange={(c) => setAuditReadyForArrest(!!c)} className="hidden" />
                        Ready for Arrest
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditProfileArrested ? 'bg-emerald-600/30 border border-emerald-500 text-emerald-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                        <Checkbox checked={auditProfileArrested} onCheckedChange={(c) => setAuditProfileArrested(!!c)} className="hidden" />
                        Arrested
                      </label>
                    </div>
                  </div>
                </div>
                {/* Critical flags */}
                <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3">
                  <Label className="text-xs text-red-400 uppercase tracking-wide mb-3 block">Critical Flags</Label>
                  <div className="flex flex-wrap gap-2">
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditArrests ? 'bg-red-600/30 border border-red-500 text-red-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditArrests} onCheckedChange={(c) => setAuditArrests(!!c)} className="hidden" />
                      Arrests
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditHateCrime ? 'bg-red-600/30 border border-red-500 text-red-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditHateCrime} onCheckedChange={(c) => setAuditHateCrime(!!c)} className="hidden" />
                      Hate Crime
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditWeapon ? 'bg-red-600/30 border border-red-500 text-red-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditWeapon} onCheckedChange={(c) => setAuditWeapon(!!c)} className="hidden" />
                      Weapon
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditInjuries ? 'bg-red-600/30 border border-red-500 text-red-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditInjuries} onCheckedChange={(c) => setAuditInjuries(!!c)} className="hidden" />
                      Injuries
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditMinorElderly ? 'bg-red-600/30 border border-red-500 text-red-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditMinorElderly} onCheckedChange={(c) => setAuditMinorElderly(!!c)} className="hidden" />
                      Minor/Elderly
                    </label>
                  </div>
                </div>
                {/* Status flags */}
                <div className="bg-purple-950/20 border border-purple-900/30 rounded-lg p-3">
                  <Label className="text-xs text-purple-400 uppercase tracking-wide mb-3 block">Status Flags</Label>
                  <div className="flex flex-wrap gap-2">
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditPdActivated ? 'bg-purple-600/30 border border-purple-500 text-purple-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditPdActivated} onCheckedChange={(c) => setAuditPdActivated(!!c)} className="hidden" />
                      PD Activated
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditHotlineCall ? 'bg-purple-600/30 border border-purple-500 text-purple-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditHotlineCall} onCheckedChange={(c) => setAuditHotlineCall(!!c)} className="hidden" />
                      Hotline Call
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditReportFiled ? 'bg-purple-600/30 border border-purple-500 text-purple-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditReportFiled} onCheckedChange={(c) => setAuditReportFiled(!!c)} className="hidden" />
                      Report Filed
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditFootageRequested ? 'bg-purple-600/30 border border-purple-500 text-purple-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditFootageRequested} onCheckedChange={(c) => setAuditFootageRequested(!!c)} className="hidden" />
                      Footage Requested
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm ${auditFootageObtained ? 'bg-purple-600/30 border border-purple-500 text-purple-400' : 'bg-slate-800/50 hover:bg-slate-700/50 border border-transparent text-slate-300'}`}>
                      <Checkbox checked={auditFootageObtained} onCheckedChange={(c) => setAuditFootageObtained(!!c)} className="hidden" />
                      Footage Obtained
                    </label>
                  </div>
                </div>
              </div>
            )}
            {/* Active filters display */}
            {(auditUnitFilter !== "all" || auditTypeFilter !== "all" || auditActivityFilter !== "both" || auditIncidentSearch || auditAddressSearch || auditStartDate || auditEndDate || auditDispatcherFilter || auditHasAnyProfile || auditSpecificProfile || auditReadyForArrest || auditProfileArrested || auditArrests || auditHateCrime || auditWeapon || auditInjuries || auditMinorElderly || auditPdActivated || auditHotlineCall || auditReportFiled || auditFootageRequested || auditFootageObtained) && (
              <div className="flex flex-wrap gap-2 mt-3">
                {auditUnitFilter !== "all" && <Badge variant="secondary" className="text-xs">Unit: {auditUnitFilter} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditUnitFilter("all")} /></Badge>}
                {auditTypeFilter !== "all" && <Badge variant="secondary" className="text-xs">Type: {auditTypeFilter} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditTypeFilter("all")} /></Badge>}
                {auditActivityFilter !== "both" && <Badge variant="secondary" className="text-xs">Activity: {auditActivityFilter === "incidents" ? "Incidents" : auditActivityFilter === "live_calls" ? "Dispatch" : "Patrol"} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditActivityFilter("both")} /></Badge>}
                {auditIncidentSearch && <Badge variant="secondary" className="text-xs">Headline: "{auditIncidentSearch}" <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditIncidentSearch("")} /></Badge>}
                {auditAddressSearch && <Badge variant="secondary" className="text-xs">Address: "{auditAddressSearch}" <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditAddressSearch("")} /></Badge>}
                {auditStartDate && <Badge variant="secondary" className="text-xs">From: {auditStartDate} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditStartDate("")} /></Badge>}
                {auditEndDate && <Badge variant="secondary" className="text-xs">To: {auditEndDate} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditEndDate("")} /></Badge>}
                {auditDispatcherFilter && <Badge variant="secondary" className="text-xs">Dispatcher: {auditDispatcherFilter} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditDispatcherFilter("")} /></Badge>}
                {auditSpecificProfile && <Badge variant="secondary" className="text-xs">Profile: {profiles.find(p => p.profile_id === auditSpecificProfile)?.name || auditSpecificProfile} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditSpecificProfile("")} /></Badge>}
                {auditHasAnyProfile && <Badge variant="secondary" className="text-xs">Has Profile <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditHasAnyProfile(false)} /></Badge>}
                {auditReadyForArrest && <Badge variant="secondary" className="text-xs">Ready for Arrest <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditReadyForArrest(false)} /></Badge>}
                {auditProfileArrested && <Badge variant="secondary" className="text-xs">Profile Arrested <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditProfileArrested(false)} /></Badge>}
                {auditArrests && <Badge variant="secondary" className="text-xs">Arrests <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditArrests(false)} /></Badge>}
                {auditHateCrime && <Badge variant="secondary" className="text-xs">Hate Crime <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditHateCrime(false)} /></Badge>}
                {auditWeapon && <Badge variant="secondary" className="text-xs">Weapon <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditWeapon(false)} /></Badge>}
                {auditInjuries && <Badge variant="secondary" className="text-xs">Injuries <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditInjuries(false)} /></Badge>}
                {auditMinorElderly && <Badge variant="secondary" className="text-xs">Minor/Elderly <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditMinorElderly(false)} /></Badge>}
                {auditPdActivated && <Badge variant="secondary" className="text-xs">PD Activated <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditPdActivated(false)} /></Badge>}
                {auditHotlineCall && <Badge variant="secondary" className="text-xs">Hotline Call <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditHotlineCall(false)} /></Badge>}
                {auditReportFiled && <Badge variant="secondary" className="text-xs">Report Filed <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditReportFiled(false)} /></Badge>}
                {auditFootageRequested && <Badge variant="secondary" className="text-xs">Footage Requested <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditFootageRequested(false)} /></Badge>}
                {auditFootageObtained && <Badge variant="secondary" className="text-xs">Footage Obtained <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setAuditFootageObtained(false)} /></Badge>}
              </div>
            )}
          </div>
          <ScrollArea className="flex-1 p-4">
            {auditLoading ? <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div> : filteredAuditData.length === 0 ? <div className="text-center py-20 text-slate-500"><Activity className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>{auditData.length === 0 ? 'Click "Search" to load audit data' : 'No results match your filters'}</p></div> : (
              <div className="space-y-2">
                {filteredAuditData.map((unit) => (
                  <Card key={unit.unit_id || unit.unit_name} className="bg-slate-950 border-slate-800">
                    <div className="p-3 cursor-pointer hover:bg-slate-900 transition-colors" onClick={() => toggleUnitExpansion(unit.unit_name)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedUnits[unit.unit_name] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="font-mono font-bold text-lg">{unit.unit_name}</span>
                          <div className="flex gap-1">
                            {unit.is_dispatcher && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500">DISP</Badge>}
                            {unit.is_investigator && <Badge variant="outline" className="text-xs text-blue-400 border-blue-500">INV</Badge>}
                            {unit.is_patrol && <Badge variant="outline" className="text-xs text-green-400 border-green-500">PATROL</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          {(auditActivityFilter === "both" || auditActivityFilter === "incidents") && (
                            <>
                              <div className="text-center"><p className="font-bold text-blue-400">{unit.incidents_responded?.length || 0}</p><p className="text-slate-500">Responded</p></div>
                              <div className="text-center"><p className="font-bold text-purple-400">{unit.incidents_investigating?.length || 0}</p><p className="text-slate-500">Investigating</p></div>
                            </>
                          )}
                          {(auditActivityFilter === "both" || auditActivityFilter === "live_calls") && (
                            <div className="text-center"><p className="font-bold text-green-400">{unit.live_calls?.length || 0}</p><p className="text-slate-500">Dispatch</p></div>
                          )}
                          {(auditActivityFilter === "both" || auditActivityFilter === "patrol") && (
                            <div className="text-center"><p className="font-bold text-amber-400">{unit.patrol_sessions?.length || 0}</p><p className="text-slate-500">Patrol</p></div>
                          )}
                          <div className="text-center"><p className="font-bold text-emerald-400">{unit.total_filtered || 0}</p><p className="text-slate-500">Total</p></div>
                        </div>
                      </div>
                    </div>
                    {expandedUnits[unit.unit_name] && (
                      <div className="px-4 pb-4 border-t border-slate-800 mt-2 pt-3">
                        <Tabs defaultValue={auditActivityFilter === "live_calls" ? "livecalls" : auditActivityFilter === "patrol" ? "patrol" : "incidents"} className="w-full">
                          <TabsList className="bg-slate-900 mb-3">
                            {(auditActivityFilter === "both" || auditActivityFilter === "incidents") && (
                              <>
                                <TabsTrigger value="incidents">Responded ({unit.incidents_responded?.length || 0})</TabsTrigger>
                                <TabsTrigger value="investigating">Investigating ({unit.incidents_investigating?.length || 0})</TabsTrigger>
                              </>
                            )}
                            {(auditActivityFilter === "both" || auditActivityFilter === "live_calls") && (
                              <TabsTrigger value="livecalls">Dispatch ({unit.live_calls?.length || 0})</TabsTrigger>
                            )}
                            {(auditActivityFilter === "both" || auditActivityFilter === "patrol") && (
                              <TabsTrigger value="patrol">Patrol ({unit.patrol_sessions?.length || 0})</TabsTrigger>
                            )}
                          </TabsList>
                          <TabsContent value="incidents" className="mt-0">{!unit.incidents_responded?.length ? <p className="text-slate-500 text-sm py-4">No incidents</p> : <div className="space-y-1 max-h-64 overflow-y-auto">{unit.incidents_responded.map(inc => <div key={inc.incident_id} className="p-2 bg-slate-900 rounded flex items-center justify-between hover:bg-slate-800 cursor-pointer" onClick={() => navigate(`/incidents/${inc.incident_id}`)}><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{inc.headline || inc.incident_id}</p><p className="text-xs text-slate-400 truncate">{inc.address}</p></div><div className="flex items-center gap-2 ml-2"><Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge>{inc.is_arrest && <Badge className="bg-red-600 text-[10px]">ARREST</Badge>}<span className="text-xs text-slate-500">{inc.date_time ? format(new Date(inc.date_time), "MMM d") : ""}</span><ExternalLink className="w-3 h-3 text-slate-400" /></div></div>)}</div>}</TabsContent>
                          <TabsContent value="investigating" className="mt-0">{!unit.incidents_investigating?.length ? <p className="text-slate-500 text-sm py-4">No cases</p> : <div className="space-y-1 max-h-64 overflow-y-auto">{unit.incidents_investigating.map(inc => <div key={inc.incident_id} className="p-2 bg-slate-900 rounded flex items-center justify-between hover:bg-slate-800 cursor-pointer" onClick={() => navigate(`/incidents/${inc.incident_id}`)}><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{inc.headline || inc.incident_id}</p></div><div className="flex items-center gap-2 ml-2"><Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge><span className="text-xs text-slate-500">{inc.date_time ? format(new Date(inc.date_time), "MMM d") : ""}</span></div></div>)}</div>}</TabsContent>
                          <TabsContent value="livecalls" className="mt-0">{!unit.live_calls?.length ? <p className="text-slate-500 text-sm py-4">No live calls</p> : <div className="space-y-1 max-h-64 overflow-y-auto">{unit.live_calls.map(lc => <div key={lc.call_id} className="p-2 bg-slate-900 rounded flex items-center justify-between"><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{lc.call_type || "Call"}</p><p className="text-xs text-slate-400 truncate">{lc.location}</p></div><div className="flex items-center gap-2 ml-2"><Badge variant="outline" className={`text-[10px] ${lc.status === 'live' ? 'border-green-500 text-green-400' : ''}`}>{lc.status}</Badge><span className="text-xs text-slate-500">{lc.created_at ? format(new Date(lc.created_at), "MMM d") : ""}</span></div></div>)}</div>}</TabsContent>
                          <TabsContent value="patrol" className="mt-0">{!unit.patrol_sessions?.length ? <p className="text-slate-500 text-sm py-4">No patrol sessions</p> : <div className="space-y-1 max-h-64 overflow-y-auto">{unit.patrol_sessions.map((ps, idx) => <div key={ps.session_id || idx} className="p-2 bg-slate-900 rounded flex items-center justify-between"><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">Patrol - {ps.car_name || "Unknown Car"}</p><p className="text-xs text-slate-400">{ps.session_start ? format(new Date(ps.session_start), "MMM d h:mm a") : ""} → {ps.session_end ? format(new Date(ps.session_end), "h:mm a") : ""}</p></div><div className="flex items-center gap-2 ml-2"><Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500">{ps.duration_minutes || 0} min</Badge></div></div>)}</div>}</TabsContent>
                        </Tabs>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Audit Export Dialog */}
      <Dialog open={showAuditExportDialog} onOpenChange={setShowAuditExportDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="uppercase tracking-wider">Export Audit Data</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Choose export format:</p>
            <div className="grid grid-cols-1 gap-3">
              <Button onClick={handleStandardExport} variant="outline" className="justify-start h-auto py-3">
                <div className="text-left">
                  <p className="font-medium">Standard Export</p>
                  <p className="text-xs text-slate-400">One row per activity (unit, incident/call details)</p>
                </div>
              </Button>
              <Button onClick={handleListFormatExport} variant="outline" className="justify-start h-auto py-3">
                <div className="text-left">
                  <p className="font-medium">List Format Export</p>
                  <p className="text-xs text-slate-400">Matrix: Units as rows, Incidents/Calls as columns (R=Responded, I=Investigating, X=Assigned)</p>
                </div>
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setShowAuditExportDialog(false)} className="w-full">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Week Export Dialog */}
      <Dialog open={showQuickExportDialog} onOpenChange={setShowQuickExportDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="uppercase tracking-wider flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-400" />Weekly Unit Summary</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">Quick export showing which units were active during a week (Friday to Friday):</p>
            <div className="space-y-2">
              <Label>Select Week</Label>
              <Select value={quickExportWeek} onValueChange={setQuickExportWeek}>
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="Choose a week..." />
                </SelectTrigger>
                <SelectContent>
                  {getWeekOptions().map((week) => (
                    <SelectItem key={week.value} value={week.value}>{week.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-800 rounded p-3 text-xs text-slate-400 space-y-1">
              <p><strong className="text-blue-400">Line 1:</strong> Units that responded to any call</p>
              <p><strong className="text-green-400">Line 2:</strong> Units that did patrol sessions</p>
              <p><strong className="text-amber-400">Line 3:</strong> Dispatch units active that week</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="ghost" onClick={() => setShowQuickExportDialog(false)} size="sm">Cancel</Button>
              <Button onClick={handleQuickWeekTextExport} disabled={!quickExportWeek || quickExportLoading} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Copy className="w-3.5 h-3.5 mr-1" />Copy
              </Button>
              <Button onClick={handleQuickWeekExport} disabled={!quickExportWeek || quickExportLoading} size="sm" className="bg-amber-600 hover:bg-amber-700">
                <Download className="w-3.5 h-3.5 mr-1" />CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="uppercase tracking-wider">Edit Unit</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label>Unit Name</Label><Input value={editUnit.name || ""} onChange={(e) => setEditUnit({ ...editUnit, name: e.target.value.toUpperCase() })} className="input-dark" /></div>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2"><Checkbox id="edit_is_dispatcher" checked={editUnit.is_dispatcher || false} onCheckedChange={(checked) => setEditUnit({ ...editUnit, is_dispatcher: checked })} /><Label htmlFor="edit_is_dispatcher">Dispatcher</Label></div>
              <div className="flex items-center gap-2"><Checkbox id="edit_is_investigator" checked={editUnit.is_investigator || false} onCheckedChange={(checked) => setEditUnit({ ...editUnit, is_investigator: checked })} /><Label htmlFor="edit_is_investigator">Investigator</Label></div>
              <div className="flex items-center gap-2"><Checkbox id="edit_is_patrol" checked={editUnit.is_patrol || false} onCheckedChange={(checked) => setEditUnit({ ...editUnit, is_patrol: checked })} /><Label htmlFor="edit_is_patrol">Patrol</Label></div>
            </div>
            <Button onClick={handleUpdateUnit} className="w-full btn-primary"><Save className="w-4 h-4 mr-1" />Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <ExportModal open={showExportDialog} onOpenChange={setShowExportDialog} csvData={exportData} filename={exportFilename} downloadUrl={exportDownloadUrl} />

      {/* ID Card dialog */}
      <Dialog open={showCreditCardDialog} onOpenChange={setShowCreditCardDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="uppercase tracking-wider">{editingCreditCard ? "Edit ID Card" : "Issue ID Card"}{selectedUnit ? ` — ${selectedUnit.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div><Label>Card Number</Label><Input value={idCardForm.card_number} onChange={(e) => setCreditCardForm({ ...idCardForm, card_number: e.target.value })} className="input-dark" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issued</Label><Input type="date" value={idCardForm.issued_at} onChange={(e) => setCreditCardForm({ ...idCardForm, issued_at: e.target.value })} className="input-dark" /></div>
              <div><Label>Expires</Label><Input type="date" value={idCardForm.expires_at} onChange={(e) => setCreditCardForm({ ...idCardForm, expires_at: e.target.value })} className="input-dark" /></div>
            </div>
            <div><Label>Returned</Label><Input type="date" value={idCardForm.returned_at} onChange={(e) => setCreditCardForm({ ...idCardForm, returned_at: e.target.value })} className="input-dark" /></div>
            <div className="flex items-center gap-2"><Checkbox id="old_card_returned" checked={idCardForm.old_card_returned} onCheckedChange={(c) => setCreditCardForm({ ...idCardForm, old_card_returned: !!c })} /><Label htmlFor="old_card_returned">Old card was returned</Label></div>
            <div><Label>Notes</Label><Input value={idCardForm.notes} onChange={(e) => setCreditCardForm({ ...idCardForm, notes: e.target.value })} className="input-dark" /></div>
            <Button onClick={saveCreditCard} className="w-full btn-primary"><Save className="w-4 h-4 mr-1" />{editingCreditCard ? "Save Changes" : "Issue ID Card"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Plaque dialog */}
      <Dialog open={showPlaqueDialog} onOpenChange={setShowPlaqueDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle className="uppercase tracking-wider">{editingPlaque ? "Edit Plaque" : "Issue Plaque"}{selectedUnit ? ` — ${selectedUnit.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div><Label>Plaque Number</Label><Input value={plaqueForm.plaque_number} onChange={(e) => setPlaqueForm({ ...plaqueForm, plaque_number: e.target.value })} className="input-dark" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issued</Label><Input type="date" value={plaqueForm.issued_at} onChange={(e) => setPlaqueForm({ ...plaqueForm, issued_at: e.target.value })} className="input-dark" /></div>
              <div><Label>Expires</Label><Input type="date" value={plaqueForm.expires_at} onChange={(e) => setPlaqueForm({ ...plaqueForm, expires_at: e.target.value })} className="input-dark" /></div>
            </div>
            <div><Label>Returned</Label><Input type="date" value={plaqueForm.returned_at} onChange={(e) => setPlaqueForm({ ...plaqueForm, returned_at: e.target.value })} className="input-dark" /></div>
            <div><Label>Notes</Label><Input value={plaqueForm.notes} onChange={(e) => setPlaqueForm({ ...plaqueForm, notes: e.target.value })} className="input-dark" /></div>
            <Button onClick={savePlaque} className="w-full btn-primary"><Save className="w-4 h-4 mr-1" />{editingPlaque ? "Save Changes" : "Issue Plaque"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
